import { useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  ChargeBeePlanId,
  PlanIdToNameMap,
  type PlanName,
  chargeBeePlans,
  isPlanEqualOrHigher,
} from '@prerender/billing-shared';
import type { DateRangePickerProps, DateRangePresetLabel, LockedPresets } from '@prerender/design-system/DateRangePicker';

import { useUpgradePlan } from '~features/UpgradePlan';
import { useCharbeePlanId, useCurrentPlan } from '../../hooks/usePrerenderUser';

const PRESET_REQUIRED_PLAN: Partial<Record<DateRangePresetLabel, ChargeBeePlanId>> = {
  'last 180 days': ChargeBeePlanId.Growth,
  'this year': ChargeBeePlanId.Pro,
  'last year': ChargeBeePlanId.Pro,
};

const UPSOLD_PLANS: readonly ChargeBeePlanId[] = [ChargeBeePlanId.Starter, ChargeBeePlanId.Growth, ChargeBeePlanId.Pro];

export const RETENTION_TIERS: ReadonlyArray<{ days: number; plan: PlanName }> = UPSOLD_PLANS.map((planId) => {
  const plan = chargeBeePlans.find((candidate) => candidate.chargeBeePlanId === planId);
  if (plan?.dataRetentionDays === undefined) throw new Error(`billing-shared has no retention for ${planId}`);
  return { days: plan.dataRetentionDays, plan: plan.name };
});

export function lockedPresetsFor(planId: ChargeBeePlanId): LockedPresets {
  const locked: LockedPresets = {};
  for (const [label, requiredPlan] of Object.entries(PRESET_REQUIRED_PLAN)) {
    if (!isPlanEqualOrHigher(planId, requiredPlan)) {
      locked[label as DateRangePresetLabel] = { tooltip: `Upgrade to ${PlanIdToNameMap[requiredPlan]}` };
    }
  }
  return locked;
}

export function explainBeyondRetention(date: Dayjs): string | undefined {
  const tier = RETENTION_TIERS.find(({ days }) => !dayjs().subtract(days, 'days').isAfter(date, 'day'));
  return tier ? `Upgrade to ${tier.plan} to select this date` : undefined;
}

export type DateRangePlanGating = Pick<
  DateRangePickerProps,
  'retentionDays' | 'lockedPresets' | 'explainBeyondRetention' | 'onUpgrade'
>;

export function useDateRangePlanGating(): DateRangePlanGating {
  const planId = useCharbeePlanId();
  const { dataRetentionDays } = useCurrentPlan();
  const { openUpgradePlan } = useUpgradePlan();

  const lockedPresets = useMemo(() => lockedPresetsFor(planId), [planId]);
  const onUpgrade = useCallback(
    (preset: DateRangePresetLabel) => {
      const requiredPlan = PRESET_REQUIRED_PLAN[preset];
      if (!requiredPlan) return;
      openUpgradePlan('dashboard', requiredPlan, {
        ctaLocation: 'Date Range Presets - Top Filters',
        ctaName: 'Upgrade',
      });
    },
    [openUpgradePlan]
  );

  return { retentionDays: dataRetentionDays, lockedPresets, explainBeyondRetention, onUpgrade };
}
